import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Stethoscope, Home, Calendar, FileText, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function PatientLayout() {
  return (
    <div className="min-h-screen flex bg-[hsl(var(--bg-base))]">
      <PatientSidebar />
      <div className="flex-1 min-w-0 flex flex-col pb-16 md:pb-0">
        <PatientHeader />
        <main className="flex-1 px-4 md:px-8 py-6 md:py-8" data-testid="patient-main">
          <Outlet />
        </main>
        <PatientBottomNav />
      </div>
    </div>
  );
}

function PatientSidebar() {
  const items = [
    { to: "/portal", end: true, icon: Home, label: "Início", testid: "p-nav-home" },
    { to: "/portal/agenda", icon: Calendar, label: "Minhas consultas", testid: "p-nav-appointments" },
    { to: "/portal/sessoes", icon: FileText, label: "Sessões", testid: "p-nav-sessions" },
  ];
  return (
    <aside data-testid="patient-sidebar" className="hidden md:flex sidebar-grain w-64 shrink-0 border-r border-[hsl(var(--border-default))] flex-col h-screen sticky top-0">
      <div className="px-5 h-16 flex items-center gap-2.5 border-b border-[hsl(var(--border-divider))]">
        <div className="w-10 h-10 rounded-xl bg-white overflow-hidden flex items-center justify-center shadow-sm p-1.5">
          <img src="/iatros-logo-icon-simple.svg" alt="Iatrós Logo" className="w-full h-full object-contain" />
        </div>
        <div className="leading-tight">
          <div className="text-[15px] font-semibold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Iatrós</div>
          <div className="text-[11px] text-[hsl(var(--text-tertiary))] -mt-0.5">Portal do paciente</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        <div className="ds-overline px-3 mb-2">Meu cuidado</div>
        {items.map(it => (
          <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-[hsl(var(--brand))] text-white shadow-sm"
                         : "text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--border-default))]/60 hover:text-[hsl(var(--text-primary))]"
              }`
            }>
            <it.icon className="w-[18px] h-[18px]" />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[hsl(var(--border-divider))]">
        <div className="rounded-lg bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] p-4">
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase opacity-80">Em breve</div>
          <div className="text-sm mt-1.5 leading-snug">Agende suas sessões direto pelo portal e adquira planos com desconto.</div>
        </div>
      </div>
    </aside>
  );
}

function PatientBottomNav() {
  const items = [
    { to: "/portal", end: true, icon: Home, label: "Início" },
    { to: "/portal/agenda", icon: Calendar, label: "Consultas" },
    { to: "/portal/sessoes", icon: FileText, label: "Sessões" },
  ];

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-t border-[hsl(var(--border-divider))] px-2 pb-safe-area-inset-bottom">
      <div className="flex justify-around items-center h-16">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 min-w-[64px] transition-colors ${
                isActive ? "text-[hsl(var(--brand))]" : "text-[hsl(var(--text-tertiary))]"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] font-medium">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function PatientHeader() {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  const logout = async () => { await signOut(); navigate("/"); };

  return (
    <header className="h-16 sticky top-0 z-20 bg-[hsl(var(--bg-base))]/80 backdrop-blur border-b border-[hsl(var(--border-divider))]">
      <div className="h-full px-4 md:px-8 flex items-center justify-between gap-4">
        {/* Mobile Logo */}
        <div className="flex md:hidden items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white overflow-hidden flex items-center justify-center shadow-sm p-1">
            <img src="/iatros-logo-icon-simple.svg" alt="Iatrós Logo" className="w-full h-full object-contain" />
          </div>
          <span className="font-bold text-[hsl(var(--text-primary))]" style={{fontFamily:"'Outfit',sans-serif"}}>Iatrós</span>
        </div>

        <div className="hidden md:block text-sm text-[hsl(var(--text-tertiary))]">Olá, {profile?.full_name?.split(" ")[0] || "paciente"}</div>
        
        <div className="relative">
          <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2.5 pl-1 pr-2.5 h-10 rounded-lg hover:bg-[hsl(var(--bg-hover))]">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand))] text-white text-xs font-semibold flex items-center justify-center">
              {(profile?.full_name || "").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase() || "?"}
            </div>
            <div className="hidden md:block text-left leading-tight">
              <div className="text-sm font-medium">{profile?.full_name}</div>
              <div className="text-[11px] text-[hsl(var(--text-tertiary))]">{profile?.email}</div>
            </div>
            <ChevronDown className="w-4 h-4 text-[hsl(var(--text-tertiary))]" />
          </button>
          {open && (
            <div className="absolute right-0 top-12 w-56 ds-card p-1 shadow-lg z-30">
              <button data-testid="patient-logout" onClick={logout} className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md hover:bg-[hsl(var(--bg-hover))] text-[hsl(var(--danger-text))]">
                <LogOut className="w-4 h-4" /> Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
